import { ApiEndpoints } from "@shared/api/api-endpoints";
import {
  authApiClient,
  bxApiClient,
  cxApiClient,
  mxApiClient,
  onboardingApiClient,
} from "@shared/api/clients/apiClientFactory";
import { QueryStringParameters } from "@shared/api/url-builder";
import { PAGE_SIZE } from "@shared/utils/constants";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import { BillCycle, OptionsData } from "./types";

export const getUtilityServices = async (): Promise<OptionsData[]> => {
  const REMOTE_UTILITY_ID = getRemoteUtilityId();
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "auth",
    `utility/services`,
    (qs: QueryStringParameters) => {
      qs.push("remote_utility_id", REMOTE_UTILITY_ID);
    },
  );
  const response = await authApiClient.get(url);

  const data = response.data?.result;
  return data.map((item: any) => ({
    id: item.name,
    name: item.name,
  }));
};

export const getCategoryConfigMap = async (): Promise<OptionsData[]> => {
  const REMOTE_UTILITY_ID = getRemoteUtilityId();
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "onboarding",
    `config-map/category_json`,
    (qs: QueryStringParameters) => {
      qs.push("remote_utility_id", REMOTE_UTILITY_ID);
      qs.push("config_level", "category");
    },
  );
  const response = await onboardingApiClient.get(url);

  const data = response.data?.result;
  return data.map((item: any) => ({
    id: item.code,
    name: item.name,
  }));
};

export const getSubCategoryConfigMap = async (
  codes: string[],
): Promise<OptionsData[]> => {
  console.log("subCategory received", codes);

  const REMOTE_UTILITY_ID = getRemoteUtilityId();
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "onboarding",
    "config-map/category_json",
    (qs) => {
      qs.push("remote_utility_id", REMOTE_UTILITY_ID);
      qs.push("config_level", "sub_category");
      if (codes) {
        if (Array.isArray(codes)) {
          codes.forEach((code) => {
            qs.push("code_list", code);
          });
        }
      }
    },
  );
  const response = await onboardingApiClient.get(url);
  const data = response.data?.result;

  return data.map((item: any) => ({
    id: item.code,
    name: item.name,
  }));
};

export const getTerritoryConfigMap = async (
  configLevel: "area" | "sub_area" | "premise" = "area",
  codeList?: string[],
): Promise<OptionsData[]> => {
  const REMOTE_UTILITY_ID = getRemoteUtilityId();
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "onboarding",
    `config-map/territory_json`,
    (qs: QueryStringParameters) => {
      qs.push("remote_utility_id", REMOTE_UTILITY_ID);
      qs.push("config_level", configLevel);
      qs.push("show_inactive", "True");

      if (codeList && codeList.length > 0) {
        codeList.forEach((code) => {
          qs.push("code_list", code);
        });
      }
    },
  );
  const response = await onboardingApiClient.get(url);

  const data = response.data?.result;

  return data.map((item: any) => ({
    id: item.code,
    name: item.name,
  }));
};

export const getBillCycleList = async (): Promise<OptionsData[]> => {
  const REMOTE_UTILITY_ID = getRemoteUtilityId();
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "bx",
    `billcycle`,
    (qs: QueryStringParameters) => {
      qs.push("remote_utility_id", REMOTE_UTILITY_ID);
      qs.push("is_active", 1);
    },
  );
  const response = await bxApiClient.get(url);

  const data = response.data?.result;
  return data.map((item: any) => ({
    id: item.id,
    name: item.billCycleName,
  }));
};

export const getBillCycleDetails = async (billId: any): Promise<BillCycle> => {
  const REMOTE_UTILITY_ID = getRemoteUtilityId();
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "bx",
    `billcycle/details/${billId}`,
  );
  const response = await bxApiClient.get(url);

  const data = response.data?.result;
  return {
    id: data.id,
    name: data.billCycleName,
    utilityServices: data.utilityServiceList,
    areas: data.area,
    subAreas: data.subArea,
    consumerCategories: data.category,
    consumerSubCategories: data.subCategory,
    premise: data.premise,
    startDate: data.startDate,
    endDate: data.endDate,
    billConfiguaration: data.billConfiguration,
    daysSinceLastBill: data.daysSinceLastBill,
    cycleDuration: data.cycleDuration,
    invoiceStartDate: data.invoiceStartDate,
    invoiceEndDate: data.invoiceEndDate,
    daysSinceLastReading: data.daysSinceLastReading,
  };
};

export const getConsumerCount = async (
  category: string[],
  premise: any[],
): Promise<any> => {
  const REMOTE_UTILITY_ID = getRemoteUtilityId();
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "bx",
    `billcycle/get-consumer-list-view-data`,
    (qs: QueryStringParameters) => {
      qs.push("remote_utility_id", REMOTE_UTILITY_ID);
      qs.push("page", "1");
      qs.push("limit", "50");
      if (category) {
        // If filter is an array, push each element
        if (Array.isArray(category)) {
          category.forEach((code) => {
            qs.push("category", code);
          });
        }
      }
      if (premise) {
        // If filter is an array, push each element
        if (Array.isArray(premise)) {
          premise.forEach((code) => {
            qs.push("premise", code);
          });
        }
      }
    },
  );
  const response = await bxApiClient.get(url);

  return response.data?.count;
};

export const submitConsumerSelection = async (payload: any) => {
  const REMOTE_UTILITY_ID = getRemoteUtilityId();
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "bx",
    `bulk-bill-generation`,
  );
  const response = await bxApiClient.post(url, payload);
  return response.data?.result;
};

export const updateConsumerSelection = async (
  billId: string | number,
  payload: any,
) => {
  const REMOTE_UTILITY_ID = getRemoteUtilityId();
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "bx",
    `bulk-bill-generation/${billId}`,
  );
  const response = await bxApiClient.patch(url, payload);
  return response.data?.result;
};

export const getPlanDashboard = async (
  billCycleId: string | number,
): Promise<any> => {
  const REMOTE_UTILITY_ID = getRemoteUtilityId();
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "bx",
    `bill-cycle/plan-dashboard`,
    (qs: QueryStringParameters) => {
      qs.push("remote_utility_id", REMOTE_UTILITY_ID);
      qs.push("bill_cycle_id", billCycleId.toString());
      // qs.push("bill_cycle_id", billCycleId.toString());
    },
  );
  const response = await bxApiClient.get(url);
  return response.data?.result;
};
export const getConsumerListViewData = async (
  billCycleId: string | number,
  billDraftId: string,
  page: number = 1,
  filters?: {
    search?: string;
    category?: string;
    subCategory?: string;
    area?: string;
    subArea?: string;
  },
): Promise<any> => {
  const REMOTE_UTILITY_ID = getRemoteUtilityId();
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "bx",
    `billcycle/get-consumer-list-view-data`,
    (qs: QueryStringParameters) => {
      qs.push("remote_utility_id", REMOTE_UTILITY_ID);
      qs.push("bill_cycle_id", billCycleId.toString());
      qs.push("bill_draft_id", billDraftId);
      qs.push("page", page.toLocaleString());
      qs.push("limit", PAGE_SIZE);

      // Add filter parameters
      if (filters) {
        if (filters.search) {
          qs.push("search_data", filters.search);
        }
        if (filters.category && filters.category.length > 0) {
          qs.push("category", filters.category);
        }
        if (filters.subCategory && filters.subCategory.length > 0) {
          qs.push("sub_category", filters.subCategory);
        }
        if (filters.area && filters.area.length > 0) {
          qs.push("area", filters.area);
        }
        if (filters.subArea && filters.subArea.length > 0) {
          qs.push("sub_area", filters.subArea);
        }
      }
    },
  );
  const response = await bxApiClient.get(url);
  return response.data;
};
export const getConsumerDashboard = async (
  billCycleId: string | number,
): Promise<any> => {
  const REMOTE_UTILITY_ID = getRemoteUtilityId();
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "bx",
    `bill-cycle/consumer-dashboard`,
    (qs: QueryStringParameters) => {
      qs.push("remote_utility_id", REMOTE_UTILITY_ID);
      qs.push("bill_cycle_id", billCycleId.toString());
    },
  );
  const response = await bxApiClient.get(url);
  return response.data?.result;
};

export const getConsumerListMissingPlan = async (
  billCycleId: string | number,
  billDraftId: string,
  page: number = 1,
): Promise<any> => {
  const REMOTE_UTILITY_ID = getRemoteUtilityId();
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "bx",
    `billcycle/get-consumer-list-view-data`,
    (qs: QueryStringParameters) => {
      qs.push("remote_utility_id", REMOTE_UTILITY_ID);
      qs.push("bill_cycle_id", billCycleId.toString());
      qs.push("is_missing_plan", "1");
      qs.push("bill_draft_id", billDraftId);
      qs.push("limit", PAGE_SIZE);
      qs.push("page", page);
    },
  );
  const response = await bxApiClient.get(url);

  return response.data;
};

export const getConsumerListMissingTariff = async (
  billCycleId: string | number,
  billDraftId: string,
  page,
): Promise<any> => {
  const REMOTE_UTILITY_ID = getRemoteUtilityId();
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "bx",
    `billcycle/get-consumer-list-view-data`,
    (qs: QueryStringParameters) => {
      qs.push("remote_utility_id", REMOTE_UTILITY_ID);
      qs.push("bill_cycle_id", billCycleId.toString());
      qs.push("is_missing_tariff", "1");
      qs.push("bill_draft_id", billDraftId);

      qs.push("limit", PAGE_SIZE);
      qs.push("page", page.toLocaleString());
    },
  );
  const response = await bxApiClient.get(url);
  return response.data;
};

export const getPlansList = async (
  page: number = 1,
  limit: number = 50,
): Promise<any> => {
  const REMOTE_UTILITY_ID = getRemoteUtilityId();
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "onboarding",
    `plan`,
    (qs: QueryStringParameters) => {
      qs.push("remote_utility_id", REMOTE_UTILITY_ID);
      qs.push("is_pagination_required", "1");
      qs.push("page", page.toString());
      qs.push("limit", limit.toString());
    },
  );
  const response = await onboardingApiClient.get(url);
  return response.data;
};

export const getRatesList = async (
  page: number = 1,
  limit: number = 50,
): Promise<any> => {
  const REMOTE_UTILITY_ID = getRemoteUtilityId();
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "onboarding",
    `rate`,
    (qs: QueryStringParameters) => {
      qs.push("remote_utility_id", REMOTE_UTILITY_ID);
      qs.push("page", page);
      qs.push("limit", limit);
    },
  );
  const response = await onboardingApiClient.get(url);
  return response.data?.result;
};

export const getOutstandingBills = async (
  billCycleId: string | number,
  limit: number = 5,
): Promise<any> => {
  const REMOTE_UTILITY_ID = getRemoteUtilityId();
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "cx",
    `outstanding-bills`,
    (qs: QueryStringParameters) => {
      qs.push("remote_utility_id", REMOTE_UTILITY_ID);
      qs.push("bill_cycle_id", billCycleId.toString());
      qs.push("limit", limit);
      qs.push("sort", "-outstanding_balance");
    },
  );
  const response = await cxApiClient.get(url);
  return response.data?.result;
};

export const getOutstandingSummary = async (
  billCycleId: string | number,
): Promise<any> => {
  const REMOTE_UTILITY_ID = getRemoteUtilityId();
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "cx",
    `outstanding/summary`,
    (qs: QueryStringParameters) => {
      qs.push("remote_utility_id", REMOTE_UTILITY_ID);
      qs.push("bill_cycle_id", billCycleId);
    },
  );
  const response = await cxApiClient.get(url);
  return response.data?.result;
};

export const getBillCycleMeterReading = async (
  queryParams: Record<string, any>,
): Promise<any> => {
  const REMOTE_UTILITY_ID = getRemoteUtilityId();
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "bx",
    `bill-cycle/meter-reading`,
    (qs: QueryStringParameters) => {
      qs.push("remote_utility_id", REMOTE_UTILITY_ID);
      Object.entries(queryParams).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== "") {
          qs.push(key, value);
        }
      });
      qs.push("limit", PAGE_SIZE);
    },
  );
  const response = await bxApiClient.get(url);
  console.log("Meter Reading Response:", response.data);
  return response.data;
};

export const getMeterTypeChoices = async (): Promise<OptionsData[]> => {
  const REMOTE_UTILITY_ID = getRemoteUtilityId();
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    `choices/meter_type`,
  );
  const response = await mxApiClient.get(url);

  const data = response.data?.result;
  return data.map((item: any) => ({
    id: item.key,
    name: item.value,
  }));
};

export const getMeterReadingMissing = async (
  billCycleId: string,
  billDraftId: string,
  page: number,
): Promise<any> => {
  const REMOTE_UTILITY_ID = getRemoteUtilityId();
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "bx",
    `bill-cycle/meter-reading`,
    (qs: QueryStringParameters) => {
      qs.push("remote_utility_id", REMOTE_UTILITY_ID);
      qs.push("bill_cycle_id", billCycleId);
      qs.push("fetch_missing_reading", "1");
      qs.push("bill_draft_id", billDraftId);

      qs.push("page", page);
      qs.push("limit", PAGE_SIZE);
    },
  );
  const response = await bxApiClient.get(url);
  return response.data;
};

export const getMeterReadingPendingValidation = async (
  billCycleId: string,
  billDraftId: string,
  page: number,
): Promise<any> => {
  const REMOTE_UTILITY_ID = getRemoteUtilityId();
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "bx",
    `bill-cycle/meter-reading`,
    (qs: QueryStringParameters) => {
      qs.push("remote_utility_id", REMOTE_UTILITY_ID);
      qs.push("bill_cycle_id", billCycleId.toString());
      qs.push("bill_draft_id", billDraftId);
      qs.push("pending_validation", "1");
      qs.push("page", page);
      qs.push("limit", PAGE_SIZE);
    },
  );
  const response = await bxApiClient.get(url);
  return response.data;
};

export const getBillCycleMeterDashboard = async (
  billCycleId: string | number,
): Promise<any> => {
  const REMOTE_UTILITY_ID = getRemoteUtilityId();
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "bx",
    `bill-cycle/meter-dashboard`,
    (qs: QueryStringParameters) => {
      qs.push("remote_utility_id", REMOTE_UTILITY_ID);
      qs.push("bill_cycle_id", billCycleId.toString());
    },
  );
  const response = await bxApiClient.get(url);
  return response.data?.result;
};

export const generateBills = async (
  scheduleId: string,
  excludedConsumers: any,
  draftId: any,
): Promise<any> => {
  const REMOTE_UTILITY_ID = getRemoteUtilityId();
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "bx",
    `bulk-bill-generation/${draftId}`,
  );
  const payload = {
    bill_generate: true,
    step: {
      step_working_on: 4,
      total_steps: 5,
    },
  };

  const response = await bxApiClient.patch(url, payload);
  return response.data?.result;
};

export const trackBillsProgress = async (taskId: string): Promise<any> => {
  const REMOTE_UTILITY_ID = getRemoteUtilityId();
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "bx",
    `track-bills-progress`,
    (qs: QueryStringParameters) => {
      qs.push("task_id", taskId);
    },
  );
  const response = await bxApiClient.get(url);
  return response.data?.result;
};

export const retryGenerateBills = async (
  payload: any,
  scheduleId: string,
): Promise<any> => {
  const REMOTE_UTILITY_ID = getRemoteUtilityId();
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "bx",
    `retry-bills/${scheduleId}`,
  );
  const response = await bxApiClient.put(url, payload);
  return response.data?.result;
};

export const downloadfailedList = async (
  taskId: string | number,
): Promise<any> => {
  const REMOTE_UTILITY_ID = getRemoteUtilityId();
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "bx",
    `bills/errors-csv`,
    (qs: QueryStringParameters) => {
      qs.push("task_id", taskId);
    },
  );
  const response = await bxApiClient.get(url);
  return response.data;
};

export const getBillGenerationSummary = async (
  billRunId: string,
): Promise<any> => {
  const REMOTE_UTILITY_ID = getRemoteUtilityId();
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "bx",
    `bill-generation-run/summary`,
    (qs: QueryStringParameters) => {
      qs.push("remote_utility_id", REMOTE_UTILITY_ID);
      qs.push("bill_run_id", billRunId);
    },
  );
  const response = await bxApiClient.get(url);
  return response.data?.result;
};

export const getBillData = async (
  queryParams: Record<string, any>,
): Promise<any> => {
  const REMOTE_UTILITY_ID = getRemoteUtilityId();
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "bx",
    `bill`,
    (qs: QueryStringParameters) => {
      qs.push("remote_utility_id", REMOTE_UTILITY_ID);
      Object.entries(queryParams).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== "") {
          // Ensures value is not null, undefined, or empty
          qs.push(key, value);
        }
      });
    },
  );
  const response = await bxApiClient.get(url);
  return response.data;
};

export const approveGenerateBills = async (
  payload: any,
  runId: string,
  id: any = null,
): Promise<any> => {
  const REMOTE_UTILITY_ID = getRemoteUtilityId();
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "bx",
    `bill-run/status/${runId}`,
    (qs: QueryStringParameters) => {
      qs.push("bill_draft_id", id);
    },
  );
  const response = await bxApiClient.put(url, payload);
  return response.data?.result;
};

export const downloadBillTemplate = async (billId: string): Promise<Blob> => {
  const REMOTE_UTILITY_ID = getRemoteUtilityId();
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "bx",
    `render-dynamic-template/${billId}`,
  );
  const response = await bxApiClient.get(url, {
    responseType: "blob",
  });
  return response.data;
};

export const getBillTemplates = async (): Promise<any> => {
  const REMOTE_UTILITY_ID = getRemoteUtilityId();
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "bx",
    `bill-config-meta`,
    (qs: QueryStringParameters) => {
      qs.push("remote_utility_id", REMOTE_UTILITY_ID);
    },
  );
  const response = await bxApiClient.get(url);

  const data = response.data?.result;
  return data.map((item: any) => ({
    value: item.id,
    label: item.configName,
    source: item.source,
  }));
};

export const getBulkBillData = async (billId: string): Promise<any> => {
  const REMOTE_UTILITY_ID = getRemoteUtilityId();
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "bx",
    `bulk-bill-generation/${billId}`,
    (qs: QueryStringParameters) => {
      qs.push("remote_utility_id", REMOTE_UTILITY_ID);
    },
  );
  const response = await bxApiClient.get(url);
  return response.data?.result;
};
